import {
    getAxisName,
    getAxisPropertiesById,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    getDynamicTitle,
    getName,
} from '@thoughtspot/chart-lib/lib/core/column-name-util';
import { getRuntimeParameterInput } from '@thoughtspot/mfe-utilities';
import { OptionsGroup } from '@thoughtspot/radiant-react/widgets/input-mentions/input-mentions';
import { Key } from 'w3c-keys';
import {
    AnswerColumn,
    ChartViz,
    Parameter,
} from '/@services/generated/graphql-types';

// Regex to match patterns like [${Title:UniqueID}]
const DYNAMIC_TITLE_REGEX = /\[\$\{([^:}]+):([^}]+)\}\]/g;

const dynamicTitleTypes = {
    PARAMETER: 'PARAMETER',
};

export interface DynamicTitleStatus {
    value: string;
    isValid: boolean;
}

export interface SectionalDynamicTitlesType {
    [columnId: string]: DynamicTitleStatus[];
}

export const getDynamicTitleOptions = (inScopeParameters: Parameter[] = []) => {
    const optionsGroup: OptionsGroup[] = [];
    const options = inScopeParameters
        .map(parameter => ({
            id: parameter.id,
            display: parameter.name,
        }))
        .sort((a, b) => a.display.localeCompare(b.display));

    optionsGroup.push({ title: dynamicTitleTypes.PARAMETER, options });

    return optionsGroup;
};

export const getLocalResolvedDynamicTitle = (
    input: string,
    /** Map of resolved values */
    valuesMap: Map<string, string>,
) => {
    const inputCopy = input.toString();
    return inputCopy.replace(
        DYNAMIC_TITLE_REGEX,
        (matched, _value, uniqueId) => {
            const value = valuesMap.get(uniqueId);
            return value ?? matched;
        },
    );
};

export const replaceDisplayValueWithDynamicTitle = (
    input: string | null | undefined,
    optionsGroup: OptionsGroup[],
) => {
    if (!input) return '';

    const findDynamicTitle = (uniqueId: string) => {
        const category = optionsGroup.find(group =>
            group.options.some(option => option.id === uniqueId),
        );
        if (!category) return null;

        return `\${${category.title}:${uniqueId}}`;
    };

    return input.replace(DYNAMIC_TITLE_REGEX, (matched, _value, uniqueId) => {
        const replacement = findDynamicTitle(uniqueId);
        return replacement ? `[${replacement}]` : matched;
    });
};

export const replaceDynamicTitleWithDisplayValue = (
    input: string | null | undefined,
    optionsGroup: OptionsGroup[],
) => {
    if (!input) return '';

    const findDisplayValue = (title: string, uniqueId: string) => {
        const category = optionsGroup.find(group => group.title === title);
        if (!category) return null;

        const matchedOption = category.options.find(
            option => option.id === uniqueId,
        );
        return matchedOption
            ? `\${${matchedOption.display}:${uniqueId}}`
            : null;
    };

    return input.replace(DYNAMIC_TITLE_REGEX, (matched, title, uniqueId) => {
        const replacement = findDisplayValue(title, uniqueId);
        return replacement ? `[${replacement}]` : matched;
    });
};

export const extractAndMatchDynamicTitle = (
    text: string,
    optionsGroup: OptionsGroup[] = [],
) => {
    const result: DynamicTitleStatus[] = [];
    let lastIndex = 0;

    if (!text) return result;

    const flattenedOptions = optionsGroup.flatMap(group => group.options);

    const matches = [...text.matchAll(DYNAMIC_TITLE_REGEX)];

    matches.forEach(match => {
        const matchIndex = match.index ?? 0;

        if (matchIndex > lastIndex) {
            result.push({
                value: text.slice(lastIndex, matchIndex),
                isValid: false,
            });
        }

        const [fullMatch, matchedWord1, matchedWord2] = match;

        const matchingOption = flattenedOptions.find(
            option => option.id === matchedWord2,
        );

        result.push({
            value: matchingOption
                ? `${Key.AtSign}${matchingOption.display}`
                : fullMatch,
            isValid: !!matchingOption,
        });

        lastIndex = matchIndex + fullMatch.length;
    });

    if (lastIndex < text.length) {
        result.push({ value: text.slice(lastIndex), isValid: false });
    }

    return result;
};

export const getTitleValue = (
    answerColumn: AnswerColumn,
    isDynamicTitleSupported: boolean,
    optionsGroup: OptionsGroup[] = [],
) => {
    if (isDynamicTitleSupported) {
        const matchedValuesArray = extractAndMatchDynamicTitle(
            getDynamicTitle(answerColumn),
            optionsGroup,
        );
        return matchedValuesArray.map(item => item.value).join('');
    }
    return getName(answerColumn);
};

/**
 * @return `dynamicTitle` with display value if presents or `AxisName` by default
 */
export const getDynamicAxisTitle = (
    chartModel: ChartViz,
    axisId: string,
    getTranslatedText: (str: string) => string,
    optionsGroup: OptionsGroup[] = [],
    dynamicTitleSupported = false,
) => {
    const axisName = getAxisName(chartModel, axisId, getTranslatedText);

    if (dynamicTitleSupported) {
        const { dynamicTitle } = getAxisPropertiesById(
            chartModel.vizProp,
            axisId,
        );

        if (dynamicTitle) {
            const dynamicAxisName = extractAndMatchDynamicTitle(
                dynamicTitle,
                optionsGroup,
            );

            return dynamicAxisName.map(e => e.value).join('');
        }
    }

    return axisName;
};

export const textContainsDynamicTitle = (text: string) =>
    new RegExp(DYNAMIC_TITLE_REGEX).test(text);

export const getParameterResolvedValue = (text: string) => {
    return text
        .replace(DYNAMIC_TITLE_REGEX, (_matched, value, _uniqueId) => {
            return `${Key.AtSign}${value}`;
        })
        .trim();
};

/**
 * Reverse of getParameterResolvedValue: converts `@ParamName` format back to
 * `[${PARAMETER:uuid}]` tokens using referencedParameters for UUID lookup.
 * Returns null when no @ParamName patterns were matched (non-dynamic title).
 */
export const reconstructDynamicName = (
    name: string | null | undefined,
    referencedParameters: Array<{ id: string; name: string }>,
): string | null => {
    if (!name || !referencedParameters?.length) {
        return null;
    }

    // Sort by name length descending: "Month Name" matches before "Month"
    const sortedParams = [...referencedParameters]
        .filter(Boolean)
        .sort((a, b) => b.name.length - a.name.length);

    const result = sortedParams.reduce((acc, param) => {
        const escapedName = param.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(`@${escapedName}`, 'g');
        return acc.replace(
            pattern,
            `[\${${dynamicTitleTypes.PARAMETER}:${param.id}}]`,
        );
    }, name);

    // Only return if reconstruction actually produced tokens
    return result !== name ? result : null;
};

export const getUpdatedParametersWithRuntimeValues = (
    inScopeParameters: Parameter[] = [],
): Parameter[] => {
    const runtimeParameterInput = getRuntimeParameterInput();
    const runtimeValueMap = new Map(
        runtimeParameterInput.map(param => [
            param.parameterName,
            param.overrideValue,
        ]),
    );

    return inScopeParameters.map(parameter => {
        const runtimeOverrideValue = runtimeValueMap.get(parameter.name);

        if (runtimeOverrideValue !== undefined) {
            return {
                ...parameter,
                overrideValue: runtimeOverrideValue,
            };
        }

        return parameter;
    });
};

export const getAnswerTitle = (
    answerSpecMetadata: { displayName?: string; name?: string },
    isDynamicAnswerTitleEnabled: boolean,
): string => {
    return isDynamicAnswerTitleEnabled
        ? answerSpecMetadata?.displayName || answerSpecMetadata?.name
        : answerSpecMetadata?.name;
};
